import {
  FlatTableHead,
  FlatTableRow,
  FlatTableHeader,
  FlatTableCheckbox,
} from 'carbon-react/esm/components/flat-table';
import Icon from 'carbon-react/esm/components/icon';
import Sort from 'carbon-react/esm/components/flat-table/sort';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { DSN_SORT_COLUMNS, DSN_SORT_COLUMN_KEYS } from '../dsnTableSorting';
import type { DsnSortColumn, DsnSortState } from '../dsnTableSorting';
import { ENV } from '../../../config/env';

const SortableHeaderButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 0;
  color: var(--colorsUtilityYin090);
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-weight: 500;
  gap: var(--spacing075);
  padding: 0;
  text-align: left;
  word-break: keep-all;

  &:hover > span:first-child {
    border-bottom: 1px solid var(--colorsUtilityYin090);
  }

  &:focus-visible {
    box-shadow: var(--focus-shadow-default);
    outline: transparent 3px solid;
    border-radius: var(--borderRadius025);
  }

  > span[data-component='icon'] {
    color: var(--colorActionMinor500);
    margin-bottom: 1px;
  }
`;

const SortIndicator = styled.span`
  align-items: center;
  display: inline-flex;
  gap: var(--spacing025);
  justify-content: center;
  min-width: 24px;
`;

interface DsnTableHeaderProps {
  selectAll: boolean;
  onSelectAllChange: (e: React.ChangeEvent<HTMLElement>) => void;
  sort: DsnSortState;
  onSort: (column: DsnSortColumn, addToExisting?: boolean) => void;
}

export const DsnTableHeader = ({
  selectAll,
  onSelectAllChange,
  sort,
  onSort,
}: DsnTableHeaderProps) => {
  const addToExistingRef = useRef(false);
  const allowMultiSort = ENV.DSN_MULTI_SORT_ENABLED;
  const activeSort = allowMultiSort ? sort : sort.slice(0, 1);
  const selectSort = (column: DsnSortColumn, addToExisting: boolean) => {
    if (allowMultiSort) {
      onSort(column, addToExisting);
    } else {
      onSort(column);
    }
  };

  return (
    <FlatTableHead>
      <FlatTableRow>
        <FlatTableCheckbox
          ariaLabelledBy="ft-header-1 ft-header-2 ft-header-3 ft-header-4 ft-header-5 ft-header-6 ft-header-7 ft-header-8 ft-header-9"
          onClick={e => e.stopPropagation()}
          as="th"
          checked={selectAll}
          onChange={onSelectAllChange}
        />
        {DSN_SORT_COLUMN_KEYS.map((column, index) => {
          const sortIndex = activeSort.findIndex(criterion => criterion.column === column);
          const criterion = activeSort[sortIndex];
          const label = DSN_SORT_COLUMNS[column].label;

          return (
            <FlatTableHeader key={column} id={`ft-header-${index + 1}`}>
              {/* FlatTableHeader derives aria-sort from its nested Carbon Sort component. */}
              {sortIndex === 0 && criterion ? (
                // Carbon Sort does not expose the event to onClick; capture keyboard/pointer modifiers.
                <div
                  onClickCapture={event => {
                    addToExistingRef.current = event.shiftKey;
                  }}
                  onKeyDownCapture={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      addToExistingRef.current = event.shiftKey;
                    }
                  }}
                >
                  <Sort
                    onClick={() => {
                      selectSort(column, addToExistingRef.current);
                      addToExistingRef.current = false;
                    }}
                    sortType={criterion.direction}
                  >
                    {label}
                  </Sort>
                </div>
              ) : (
                <SortableHeaderButton
                  type="button"
                  aria-label={
                    criterion
                      ? `${label}, critère de tri ${sortIndex + 1}, ordre ${criterion.direction === 'ascending' ? 'croissant' : 'décroissant'}`
                      : undefined
                  }
                  onClick={event => selectSort(column, event.shiftKey)}
                >
                  <span>{label}</span>
                  <SortIndicator aria-hidden="true">
                    {criterion && (
                      <>
                        <Icon
                          type={criterion.direction === 'ascending' ? 'sort_up' : 'sort_down'}
                        />
                        {sortIndex + 1}
                      </>
                    )}
                  </SortIndicator>
                </SortableHeaderButton>
              )}
            </FlatTableHeader>
          );
        })}
        <FlatTableHeader id="ft-header-10">Actions</FlatTableHeader>
      </FlatTableRow>
    </FlatTableHead>
  );
};
